import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  MessageSquare, 
  Send, 
  Phone,
  Twitter,
  Facebook,
  HelpCircle,
  Shield,
  Zap,
  Users
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    toast.success('Message sent successfully! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  const contactMethods = [
    {
      icon: Mail,
      title: 'Email Us',
      description: 'Get a response within 24 hours',
      value: 'support@web3ledger.com',
      link: 'mailto:support@web3ledger.com'
    },
    {
      icon: Phone,
      title: 'WhatsApp',
      description: 'Chat with us directly',
      value: '+1 606 460 8993',
      link: 'https://wa.me/16064608993'
    },
    {
      icon: Facebook,
      title: 'Facebook',
      description: 'Follow our page',
      value: 'fb.com/Web3Ledger',
      link: 'https://facebook.com/web3ledger'
    },
    {
      icon: Twitter,
      title: 'Twitter',
      description: 'Follow us for updates',
      value: '@Web3Ledger',
      link: 'https://twitter.com/web3ledger'
    }
  ];

  const faqItems = [
    {
      question: 'How do I connect my wallet?',
      answer: 'Click on "Get Started" and follow the prompts to connect your preferred wallet like MetaMask, Trust Wallet, or others.'
    },
    {
      question: 'Is my private key safe?',
      answer: 'Absolutely. Your private keys never leave your device. We use a non-custodial model where you maintain full control.'
    },
    {
      question: 'What blockchains do you support?',
      answer: 'We support 50+ chains including Ethereum, Bitcoin, BNB Chain, Polygon, Solana, Avalanche, and many more.'
    },
    {
      question: 'How do I recover my wallet?',
      answer: 'You can recover your wallet using your 12 or 24-word recovery phrase. We cannot recover your wallet if you lose this phrase.'
    }
  ];

  const supportOptions = [
    {
      icon: HelpCircle,
      title: 'Help Center',
      description: 'Browse our comprehensive guides and tutorials'
    },
    {
      icon: Shield,
      title: 'Security Issues',
      description: 'Report vulnerabilities to security@web3ledger.com'
    },
    {
      icon: Zap,
      title: 'Feature Requests',
      description: 'Share your ideas via our contact form'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'Contact us on WhatsApp: +1 606 460 8993'
    }
  ];

  return (
    <div className="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="hero-glow"></div>
        <div className="container">
          <motion.div 
            className="contact-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">
              <MessageSquare size={14} />
              Get in Touch
            </span>
            <h1>Contact Us</h1>
            <p>Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="contact-methods-section">
        <div className="container">
          <div className="contact-methods-grid">
            {contactMethods.map((method, index) => (
              <motion.a
                key={index}
                href={method.link}
                className="contact-method-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="method-icon">
                  <method.icon size={24} />
                </div>
                <div className="method-content">
                  <h3>{method.title}</h3>
                  <p>{method.description}</p>
                  <span className="method-value">{method.value}</span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <motion.div 
              className="contact-form-wrapper"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="form-header">
                <h2>Send us a Message</h2>
                <p>Fill out the form below and we'll get back to you within 24 hours.</p>
              </div>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Your Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="partnership">Partnership</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us how we can help you..."
                    rows="5"
                    required
                  ></textarea>
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-small"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* FAQ Section */}
            <motion.div 
              className="contact-faq"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="faq-header">
                <h2>Frequently Asked Questions</h2>
                <p>Quick answers to common questions</p>
              </div>
              
              <div className="faq-list">
                {faqItems.map((item, index) => (
                  <div key={index} className="faq-item">
                    <h4>{item.question}</h4>
                    <p>{item.answer}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Support Options */}
      <section className="support-options-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Other Ways to Get Help</h2>
            <p>Choose the support option that best fits your needs</p>
          </motion.div>
          
          <div className="support-options-grid">
            {supportOptions.map((option, index) => (
              <motion.div
                key={index}
                className="support-option-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="option-icon">
                  <option.icon size={24} />
                </div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="contact-cta">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>Ready to Get Started?</h2>
            <p>Join thousands of users who trust Web3 Ledger for their digital assets.</p>
            <div className="cta-actions">
              <a href="/get-started" className="btn btn-primary">
                Get Started
              </a>
              <a href="/ledger" className="btn btn-secondary">
                Learn More
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
